import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

const AuthContext = createContext(null);

// Token storage keys (in-memory, not localStorage for security)
const TOKEN_STORAGE = {
  accessToken: null,
  refreshToken: null,
  user: null,
  userType: null
};

/**
 * Parse JWT payload without verification (for expiry check)
 */
const parseJwtPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired (with 30 second buffer)
 */
const isTokenExpired = (token) => {
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) return true;

  // Consider expired 30 seconds before actual expiry for safety
  return payload.exp * 1000 < Date.now() + 30000;
};

/**
 * AuthProvider component that manages authentication state
 * Supports both session-based (web) and JWT-based (mobile) auth
 */
export const AuthProvider = ({ children, authMode = 'session' }) => {
  const [user, setUser] = useState(TOKEN_STORAGE.user);
  const [userType, setUserType] = useState(TOKEN_STORAGE.userType);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state
  useEffect(() => {
    // For session mode, we rely on cookies
    // For JWT mode, tokens are stored in memory (lost on refresh)
    if (authMode === 'jwt' && TOKEN_STORAGE.accessToken) {
      setUser(TOKEN_STORAGE.user);
      setUserType(TOKEN_STORAGE.userType);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, [authMode]);

  /**
   * Login with credentials
   * @param {Object} credentials - { type: 'kid'|'parent', username, password }
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const login = useCallback(async (credentials) => {
    try {
      const params = {
        ...credentials,
        ...(authMode === 'jwt' ? { auth_mode: 'jwt' } : {})
      };

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify(params)
      });

      const data = await response.json();

      if (response.ok) {
        if (authMode === 'jwt' && data.access_token) {
          // JWT mode - store tokens in memory
          TOKEN_STORAGE.accessToken = data.access_token;
          TOKEN_STORAGE.refreshToken = data.refresh_token;
          TOKEN_STORAGE.user = data.user;
          TOKEN_STORAGE.userType = data.user_type;

          setUser(data.user);
          setUserType(data.user_type);
          setIsAuthenticated(true);
        } else {
          // Session mode - session cookie handles auth
          setIsAuthenticated(true);
        }
        return { success: true, data };
      }

      return { success: false, error: data.error || 'Login failed' };
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }, [authMode]);

  /**
   * Logout - clear tokens and session
   */
  const logout = useCallback(async () => {
    try {
      // Call server logout endpoint
      await fetch('/logout', {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      });
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    }

    // Clear local state
    TOKEN_STORAGE.accessToken = null;
    TOKEN_STORAGE.refreshToken = null;
    TOKEN_STORAGE.user = null;
    TOKEN_STORAGE.userType = null;

    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);

    // Redirect to login
    window.location.href = '/login';
  }, []);

  /**
   * Get current access token (refreshes if expired)
   * @returns {Promise<string|null>}
   */
  const getAccessToken = useCallback(async () => {
    if (authMode !== 'jwt') {
      return null; // Session mode doesn't use tokens
    }

    const currentToken = TOKEN_STORAGE.accessToken;

    if (!currentToken) {
      return null;
    }

    // Check if token is expired
    if (!isTokenExpired(currentToken)) {
      return currentToken;
    }

    // Try to refresh
    const refreshToken = TOKEN_STORAGE.refreshToken;
    if (!refreshToken) {
      logout();
      return null;
    }

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        TOKEN_STORAGE.accessToken = data.access_token;
        TOKEN_STORAGE.refreshToken = data.refresh_token;
        return data.access_token;
      } else {
        // Refresh failed, logout
        logout();
        return null;
      }
    } catch (error) {
      console.error('[Auth] Token refresh error:', error);
      logout();
      return null;
    }
  }, [authMode, logout]);

  /**
   * Handle OAuth callback (extract tokens from URL fragment)
   * @param {Object} tokens - { accessToken, refreshToken }
   */
  const handleOAuthCallback = useCallback((tokens) => {
    if (tokens.accessToken) {
      TOKEN_STORAGE.accessToken = tokens.accessToken;
      TOKEN_STORAGE.refreshToken = tokens.refreshToken;

      // Decode user info from token
      const payload = parseJwtPayload(tokens.accessToken);
      if (payload) {
        TOKEN_STORAGE.userType = payload.type;
        setUserType(payload.type);
      }

      setIsAuthenticated(true);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    userType,
    loading,
    isAuthenticated,
    authMode,
    login,
    logout,
    getAccessToken,
    handleOAuthCallback
  }), [user, userType, loading, isAuthenticated, authMode, login, logout, getAccessToken, handleOAuthCallback]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
